import { useAppSelector, useAppDispatch } from "../redux/hooks";
import { useState, useCallback, useEffect, useRef, memo } from "react";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import widgetRegistry, { WidgetType } from "../widgets/widgetRegistry";
import {
  updateWidgetData,
  updateSiteContentRequest,
} from "../redux/slices/siteContentSlice";
import { current } from '@reduxjs/toolkit';
import { toggleEditMode } from "../redux/slices/editModeSlice";
import { toggleNavEditMode } from "../redux/slices/navEditModeSlice";
import { toggleFooterEditMode } from "../redux/slices/footerEditModeSlice";
import { addToCart, openCart } from "../redux/slices/cartSlice";
import { createCartItem } from "../utils/cartUtils";
import { shouldShowSpiceLevel } from "../utils/spiceUtils";
import { filterEnabledModifiers, isModifierRequired } from "../utils/modifierUtils";
import { WidgetSelectionModal, FloatingActionButtons, ConfirmDeleteModal } from "../components";
import { AddToCartModal } from "../components/AddToCartModal";
import { useMenuAndComboData } from "../hooks/useMenuAndComboData";
import { useWidgetDataUpdater } from "../hooks/useWidgetDataUpdater";
import { useWidgetSections } from "../hooks/useWidgetSections";
import { shouldShowAddDeleteControls } from "../utils/widgetVisibilityUtils";
import { getWidgetIndexForOperation } from "../utils/widgetIndexUtils";

// ---------------- Widget Renderer ----------------
const WidgetRenderer = memo(function WidgetRenderer({
  widget,
  index,
  isEditing,
  onUpdate,
  onNavClick,
  onLogoClick,
  onBannerButtonClick,
  onAddToCart,
}: {
  widget: any;
  index: number;
  isEditing: boolean;
  onUpdate: (newData: any) => void;
  onNavClick?: (item: any) => void;
  onLogoClick?: () => void;
  onBannerButtonClick?: (button: any, index: number) => void;
  onAddToCart?: (item: any) => void;
}) {
  const { type, data } = widget;
  
  if (!type) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Widget type is missing</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // Extract base type from unique type (e.g., "EssentialBanner_A1B2C3D4" -> "EssentialBanner")
  const baseType = type.includes('_') ? type.split('_')[0] : type;
  
  // Use widget registry with base type for lazy loading
  const LazyComponent = widgetRegistry[baseType as WidgetType];

  if (!LazyComponent) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Type: {type}</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div key={`widget-${type}-${index}`} className="w-full">
      <LazyComponent
        data={data}
        isEditing={isEditing}
        onUpdate={onUpdate}
        onChange={onUpdate}
        onNavClick={onNavClick}
        onLogoClick={onLogoClick}
        onBannerButtonClick={onBannerButtonClick}
        onAddToCart={onAddToCart}
        styles={{ wrapper: { minHeight: "100vh" } }}
      />
    </div>
  );
});

// ---------------- Home Page ----------------
export default function HomePage() {
  const { data: siteContent, fetchLoading, updateLoading } = useAppSelector((state) => state?.siteContent);
  const {isEditMode} = useAppSelector((state) => state?.editMode);
  const { isEditControl } = useAppSelector((state) => state?.editControl);
  const isNavEditMode = useAppSelector((state) => state?.navEditMode?.isNavEditMode);
  const isFooterEditMode = useAppSelector((state) => state?.footerEditMode?.isFooterEditMode);
  const dispatch = useAppDispatch();
  const appNavigate = useAppNavigate();

  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [pendingAddIndex, setPendingAddIndex] = useState<number | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | undefined>(undefined);
  const [modalItem, setModalItem] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSaving = useRef(false);

  // 🔄 Use custom hooks for data management
  const { menuData, comboData, menuLoading, comboLoading } = useMenuAndComboData();
  
  // 🔄 Use custom hook for widget data updates
  useWidgetDataUpdater({
    siteContent,
    menuData,
    comboData,
    menuLoading,
    comboLoading,
  });

  // ✨ Use widget sections hook for add/delete operations
  const { addSection, deleteSection, widgets, hasWidgets } = useWidgetSections();

  //  Use processed widgets directly from Redux state
  const originalWidgets = siteContent?.widgets ?? [];
  const allHomeWidgets = siteContent?.homeWidgets ?? [];
  console.log('######allHomeWidgets########',allHomeWidgets);
  console.log('######widgets########',widgets);
  const isReady = !fetchLoading && siteContent !== null;


  // 🔧 Update widget data - Type-based lookup matching Redux logic
  const handleWidgetUpdate = useCallback(
    (displayIndex: number, newData: any) => {
      // Step 1: Get widget from allHomeWidgets using display index
      const homeWidget = allHomeWidgets[displayIndex];
      if (!homeWidget) {
        console.warn('[handleWidgetUpdate] Widget not found at display index:', displayIndex);
        return;
      }
      
      // Step 2: Find that widget's type in siteContent.widgets (the actual Redux array)
      const actualIndex = siteContent?.widgets?.findIndex(
        (w: any) => w.type === homeWidget.type
      );
      
      if (actualIndex === undefined || actualIndex === -1) {
        console.warn('[handleWidgetUpdate] Widget type not found in siteContent.widgets:', homeWidget.type);
        return;
      }
      
      // Step 3: Safely unwrap any Immer proxies
      let cleanData;
      try {
        cleanData = current(newData);
      } catch (error) {
        // If current() fails, it's already a plain object
        cleanData = newData;
      }
      
      // Step 4: Update - Redux will update both widgets & homeWidgets arrays automatically
      dispatch(updateWidgetData({ index: actualIndex, data: cleanData }));
    },
    [dispatch, allHomeWidgets, siteContent]
  );

  // 💾 Save changes - Unified save that coordinates with nav and footer edit modes
  const handleSaveChanges = useCallback(() => {
    if (siteContent && !fetchLoading) {
      isSaving.current = true;
      
      // Toggle off nav edit mode if it's active
      if (isNavEditMode) {
        dispatch(toggleNavEditMode());
      }
      
      // Toggle off footer edit mode if it's active
      if (isFooterEditMode) {
        dispatch(toggleFooterEditMode());
      }
      
      // Save all changes
      dispatch(updateSiteContentRequest());
    }
  }, [dispatch, siteContent, fetchLoading, isNavEditMode, isFooterEditMode]);

  // ✏️ Toggle edit mode
  const handleEditModeToggle = useCallback(() => {
    if (isEditMode) {
      handleSaveChanges();
    } else {
      dispatch(toggleEditMode());
    }
  }, [isEditMode, handleSaveChanges, dispatch]);

  // ➕ Show widget selection modal
  const handleShowAddModal = useCallback((afterIndex?: number) => {
    setPendingAddIndex(afterIndex);
    setShowWidgetModal(true);
  }, []);

  // ✅ Handle widget selection from modal - simplified with hook
  const handleSelectWidget = useCallback((widgetType: string) => {
    addSection(widgetType, pendingAddIndex);
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, [addSection, pendingAddIndex]);

  // ❌ Close modal
  const handleCloseModal = useCallback(() => {
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, []);

  // 🗑️ Show delete confirmation modal
  const handleShowDeleteModal = useCallback((displayIndex: number) => {
    setPendingDeleteIndex(displayIndex);
    setShowDeleteModal(true);
  }, []);

  // ✅ Confirm delete - actually delete the section
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteIndex !== undefined) {
      deleteSection(pendingDeleteIndex);
    }
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, [deleteSection, pendingDeleteIndex]);

  // ❌ Cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, []);

  // 🧭 Navigation handlers
  const handleNavClick = useCallback((item: any) => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Map navigation URLs to router paths
    const urlToPathMap: { [key: string]: string } = {
      '/': '/',
      '/home': '/',
      '/menu': '/menu',
      '/contact': '/contact',
      '/blog': '/blog',
      '/our-story': '/our-story',
      '/story': '/our-story',
      '/gallery': '/gallery',
      '/events': '/events',
      '/testimonials': '/testimonials',
    };

    const path = urlToPathMap[item.url] || item.url;

    // Handle external URLs
    if (item.url.startsWith('http://') || item.url.startsWith('https://')) {
      window.open(item.url, '_blank');
      return;
    }

    // Handle internal navigation with mode awareness
    appNavigate(path);
  }, [appNavigate, isEditMode]);

  const handleLogoClick = useCallback(() => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Navigate to home page with mode awareness
    appNavigate('/');
  }, [appNavigate, isEditMode]);

  // 🎯 Banner button click handler - navigate to menu page
  const handleBannerButtonClick = useCallback((button: any, index: number) => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Navigate to menu page with mode awareness
    appNavigate('/menu');
  }, [appNavigate, isEditMode]);

  // 🛒 Cart functionality - Check if item needs customization (exact copy from MenuPage)
  const needsCustomization = useCallback((item: any): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    const hasRequiredModifiers = enabledModifiers.some(modifier => isModifierRequired(modifier));
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  // 🛒 Main add to cart handler - handles both menu items and combo items
  const handleAddToCart = useCallback((item: any) => {
    // Check if this is a combo item (has combo_id and title) or regular menu item (has pk_id and name)
    const isComboItem = item.combo_id && item.title;
    
    if (isComboItem) {
      // Handle combo items - combos don't need customization, add directly to cart
      const parsedPrice = typeof item.combo_price === "string"
        ? parseFloat(item.combo_price.replace(/[^0-9.-]+/g, ""))
        : item.combo_price;
      const comboPrice = isNaN(parsedPrice) ? 0 : parsedPrice;
      
      const cartItem = {
        item: {
          ...item,
          pk_id: item.combo_id, // Use combo_id as pk_id for cart compatibility
          name: item.title, // Use title as name for cart compatibility
          price: comboPrice.toString(), // Ensure price is string for consistency
        },
        selected: {
          spice: null,
          modifiers: [],
        },
        totalPrice: comboPrice,
        quantity: 1,
        cartItemId: `${item.combo_id}-combo-${Date.now()}`,
      };
      
      dispatch(addToCart(cartItem));
      dispatch(openCart()); // Auto-open cart
    } else {
      // Handle regular menu items
      if (needsCustomization(item)) {
        // Open modal for customization
        setModalItem(item);
        setIsModalOpen(true);
      } else {
        // Add directly to cart for simple items
        const cartItem = {
          item,
          selected: {
            spice: null,
            modifiers: [],
          },
          totalPrice: parseFloat(item.price),
          quantity: 1,
          cartItemId: `${item.pk_id}-basic-${Date.now()}`,
        };
        
        dispatch(addToCart(cartItem));
        dispatch(openCart()); // Auto-open cart
      }
    }
  }, [dispatch, needsCustomization]);

  // 🛒 Modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  // 🛒 Modal add to cart handler (exact copy from MenuPage)
  const handleModalAddToCart = useCallback((
    item: any, 
    quantity: number, 
    spiceLevel: number | null, 
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal and open cart
    setIsModalOpen(false);
    setModalItem(null);
    dispatch(openCart());
  }, [dispatch]);

  // � Exit edit mode after save is completed
  useEffect(() => {
    if (isSaving.current && !updateLoading && !fetchLoading) {
      dispatch(toggleEditMode());
      isSaving.current = false;
    }
  }, [updateLoading, fetchLoading, dispatch]);

  // 🚫 Early returns after all hooks
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (!hasWidgets) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 font-medium mb-4">No home widgets available</p>
          {isEditMode && (
            <button
              onClick={() => handleShowAddModal()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Your First Widget
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="homepage w-full min-h-screen relative">
    
      {/* Unified Floating Action Buttons - Show when edit control is enabled and not in nav edit mode */}
      {!isNavEditMode && isEditControl && (
        <FloatingActionButtons
          isEditMode={isEditMode}
          onEditToggle={handleEditModeToggle}
          disabled={updateLoading || fetchLoading}
          position="bottom-right"
          showBrandPicker={true}
        />
      )}

      {/* Render Widgets */}
      {widgets.map((widget: any, index: number) => (
        <div key={`widget-container-${index}`} className="relative">
          <WidgetRenderer
            widget={widget}
            index={index}
            isEditing={isEditMode}
            onUpdate={(newData) => handleWidgetUpdate(index, newData)}
            onNavClick={handleNavClick}
            onLogoClick={handleLogoClick}
            onBannerButtonClick={handleBannerButtonClick}
            onAddToCart={handleAddToCart}
          />

        
          {isEditMode && shouldShowAddDeleteControls(widget, isEditMode) && (
            <div className="flex justify-center gap-6 py-4 bg-gray-50 border-t border-b border-gray-200">
              <button
                onClick={() => handleShowAddModal(index)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                title="Add section after this one"
              >
                <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-medium text-sm">Add Section</span>
              </button>

              {widgets.length > 1 && (
                <button
                  onClick={() => handleShowDeleteModal(index)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                  title="Delete this section"
                >
                  <FiTrash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">Delete Section</span>
                </button>
              )}
            </div>
          )}
        </div>
      ))}


      {/* Widget Selection Modal */}
      <WidgetSelectionModal
        isOpen={showWidgetModal}
        onClose={handleCloseModal}
        onSelectWidget={handleSelectWidget}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      
      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        item={modalItem}
        onClose={handleModalClose}
        onAddToCart={handleModalAddToCart}
      />
    </div>
  );
}
